import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton } from '../componentes/ui/basicos';
import { Campo } from '../componentes/ui/formulario';
import { useTextoDeError } from '../componentes/ui/estados';
import { useSesion } from '../sesion/ProveedorDeSesion';
import { Marca, SelloDeActualSoft } from '../componentes/Marca';

interface RespuestaDeAcceso {
  readonly tokenDeAcceso?: string;
  readonly desafio?: string;
}

/**
 * Entrada al sistema.
 *
 * Un solo formulario que se convierte en el del segundo factor cuando hace falta, en vez
 * de mandar a otra pantalla: la persona ya está escribiendo, y cambiarle el contexto para
 * pedirle seis dígitos más es fricción sin motivo.
 */
export function Entrada(): React.ReactElement {
  const { t } = useTranslation();
  const { entrar } = useSesion();
  const textoDeError = useTextoDeError();

  const [correo, setCorreo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [codigo, setCodigo] = useState('');
  const [desafio, setDesafio] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async (evento: React.FormEvent): Promise<void> => {
    evento.preventDefault();
    setEnviando(true);
    setError(null);

    try {
      if (desafio === null) {
        const respuesta = await pedir<RespuestaDeAcceso>('/acceso', {
          method: 'POST',
          cuerpo: { correo, contrasena },
        });
        if (respuesta.desafio !== undefined) {
          setDesafio(respuesta.desafio);
          return;
        }
        if (respuesta.tokenDeAcceso !== undefined) await entrar(respuesta.tokenDeAcceso);
        return;
      }

      const respuesta = await pedir<{ tokenDeAcceso: string }>('/acceso/segundo-factor', {
        method: 'POST',
        cuerpo: { desafio, codigo },
      });
      await entrar(respuesta.tokenDeAcceso);
    } catch (fallo: unknown) {
      setError(textoDeError(fallo));
    } finally {
      setEnviando(false);
    }
  };

  return (
    /*
      Dos paneles: la marca a la izquierda, el trabajo a la derecha.

      **En pantalla chica se apila y el panel de marca se achica a una franja**, no desaparece: el
      acceso es la única pantalla donde el producto se presenta, y una tablet de mostrador tiene
      derecho a ver de qué sistema está entrando.
    */
    <div className="grid min-h-dvh lg:grid-cols-2">
      <div className="flex flex-col items-center justify-center gap-6 bg-marca-fondo px-8 py-10 text-center">
        <Marca forma="completo" tinta="clara" className="w-56 max-w-full lg:w-72" />
        <p className="max-w-xs text-cuerpo text-white/70">{t('acceso.presentacion')}</p>
        {/* De quien fabrica el producto, con la marca y no con el formulario: es lo que dice de
            dónde sale el sistema, y va del lado de la identidad. */}
        <SelloDeActualSoft tinta="clara" className="mt-6 w-36 max-w-full opacity-70" />
      </div>

      <div className="flex items-center justify-center px-4 py-10">
        <form
          onSubmit={(evento) => void enviar(evento)}
          className="w-full max-w-sm rounded-marco border border-borde bg-superficie p-6"
        >
          <h1 className="mb-1 text-titulo font-semibold tracking-tight">{t('acceso.titulo')}</h1>
          <p className="mb-5 text-apoyo text-texto-tenue">
            {desafio === null ? t('acceso.subtitulo') : t('acceso.subtituloSegundoFactor')}
          </p>

          <div className="flex flex-col gap-3">
            {desafio === null ? (
              <>
                <Campo
                  etiqueta={t('acceso.correo')}
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  required
                  // Excepción deliberada a jsx-a11y/no-autofocus: esta pantalla tiene un
                  // solo propósito y el primer campo es siempre el próximo paso. Obligar a
                  // buscar el campo con el mouse en un mostrador es peor que el riesgo que
                  // la regla previene, que es desorientar en páginas con mucho contenido.
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  value={correo}
                  onChange={(evento) => {
                    setCorreo(evento.target.value);
                  }}
                />
                <Campo
                  etiqueta={t('acceso.contrasena')}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={contrasena}
                  onChange={(evento) => {
                    setContrasena(evento.target.value);
                  }}
                />
              </>
            ) : (
              <Campo
                etiqueta={t('acceso.codigo')}
                // El teclado numérico en una tablet de mostrador ahorra un toque cada vez.
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={16}
                required
                // eslint-disable-next-line jsx-a11y/no-autofocus -- misma excepción
                autoFocus
                value={codigo}
                onChange={(evento) => {
                  setCodigo(evento.target.value);
                }}
                ayuda={t('acceso.ayudaCodigo')}
              />
            )}

            {error === null ? null : (
              <p role="alert" className="text-xs text-mal">
                {error}
              </p>
            )}

            <Boton type="submit" tono="principal" cargando={enviando} className="mt-1 w-full">
              {desafio === null ? t('acceso.entrar') : t('acceso.confirmar')}
            </Boton>
          </div>
        </form>
      </div>
    </div>
  );
}
